import React, { useLayoutEffect, useMemo, useState } from 'react'

import styled from 'styled-components'

import { InvestmentFrequency } from 'generated/types'

import { Bars } from './Bars/BarsContainer'
import { calcBarComposition } from './helpers/calcBarComposition'
import { calcSliderSlopeIntercept } from './helpers/calcSliderSlopeIntercept'
import { generateYearlyInvestmentProjection } from './helpers/generateYearlyInvestmentProjection'
import { InvestmentProjection } from './helpers/types'
import { Labels } from './Labels/Labels'
import { Slider } from './Slider/Slider'

const GraphContainer = styled.div`
  bottom: 0;
  left: 0;
  position: absolute;
  width: 100%;
`

/* Top offset the height of <Label /> */
const SliderContainer = styled('div')<{ height: number }>`
  height: ${(props) => `${props.height}px`};
  position: relative;
  top: -30px;
`

const getSliderMax = (investmentAmount: number) => {
  if (investmentAmount > 2300) {
    return 50000
  } else if (investmentAmount > 450) {
    return 2500
  } else {
    return 500
  }
}

export const PotentialGraph = ({
  age,
  ageOfTransfer,
  barWidth,
  currentBalance: startingAmount,
  height,
  recurringInvestmentSettings,
  smartDepositAmount,
  width,
}: {
  age: number
  ageOfTransfer: number
  barWidth: number
  currentBalance: number
  height: number
  recurringInvestmentSettings: {
    amount: number
    frequency: string
  }
  smartDepositAmount?: number
  width: number
}): React.ReactElement => {
  const [isResizing, setIsResizing] = useState(false)
  const investmentAmount =
    recurringInvestmentSettings?.amount || 0 + (smartDepositAmount || 0)
  const recurringInvestmentFrequency =
    (recurringInvestmentSettings?.frequency as InvestmentFrequency) ||
    InvestmentFrequency.Weekly

  const { barsCompositions, sliderSlopeInterceptList, yearlyProjection } =
    useMemo(() => {
      const maxInvestmentAmount = getSliderMax(investmentAmount)

      const yearlyProjection = generateYearlyInvestmentProjection({
        investmentAmount,
        recurringInvestmentFrequency,
        startingAmount,
      })

      const barsCompositions = yearlyProjection.map(
        (yearData: InvestmentProjection) => {
          return calcBarComposition({
            currentProjection: yearData,
            graphHeight: height,
            investmentAmount,
            maxInvestmentAmount,
            maxProjection: yearlyProjection[yearlyProjection.length - 1],
          })
        },
      )

      const sliderSlopeInterceptList = calcSliderSlopeIntercept({
        barWidth,
        barsCompositions,
        graphWidth: width,
      })

      return {
        barsCompositions,
        sliderSlopeInterceptList,
        yearlyProjection,
      }
    }, [
      barWidth,
      height,
      investmentAmount,
      recurringInvestmentFrequency,
      startingAmount,
      width,
    ])

  const projectionRemainZero = startingAmount + investmentAmount === 0

  useLayoutEffect(() => {
    let resizingTimer: ReturnType<typeof setTimeout>

    const resizeUpdate = () => {
      clearTimeout(resizingTimer)
      setIsResizing(true)
      resizingTimer = setTimeout(() => setIsResizing(false), 200)
    }

    window.addEventListener('resize', resizeUpdate)
    resizeUpdate()
    return () => window.removeEventListener('resize', resizeUpdate)
  }, [])

  return (
    <>
      <SliderContainer
        height={barsCompositions[barsCompositions.length - 1].barHeight}
      >
        {!isResizing ? (
          <Slider
            ageOfTransfer={ageOfTransfer}
            barCount={barsCompositions.length}
            barWidth={barWidth}
            currentAge={age}
            graphHeight={
              barsCompositions[barsCompositions.length - 1].barHeight
            }
            graphWidth={width}
            projectionRemainZero={projectionRemainZero}
            slopeInterceptList={sliderSlopeInterceptList}
            yearlyProjection={yearlyProjection}
          />
        ) : null}
      </SliderContainer>
      <GraphContainer>
        <Bars
          ageOfTransfer={ageOfTransfer}
          barWidth={4}
          barsCompositions={barsCompositions}
          currentAge={age}
        />
        <Labels currentAge={age} graphWidth={width} />
      </GraphContainer>
    </>
  )
}
